/*
  When creating a validator, you can access data in one of two ways. If you have a flat object (usually a custom form class) that doesn't have any sub-properties,
  you can the key of the validator to look up the property to validate in the form. The object you're validating would need to look like this:
  
    {
      title: 'this is a title',
      fileNames: ['file1', 'file2'],
      invoiceAdvisorName: 'John'
    }

  The validator would look like this:

    validations = FormTypes.Validators {
      title: { required: true },
      fileNames: { required: true, min: 1 },
      invoiceAdvisorName: { required: true }
    }

  If you want to validate against a more complex, multi-level object, you can use the accessor property. In the case where you have an object like a deliverable:

    {
      title: 'this is a title',
      evidence {
        fileNames: ['file1', 'file2']
      },
      invoice: {
        advisor: {
          firstName: 'John'
        }
      }
    }

  You could write validators with an accessor property to tell the validation library how to find the data it needs to validate (note you'll probably want to pass
  in a type in the <> brackets so you have type awareness in the accessor):

    validations = FormTypes.Validators<DeliverableTypes.Deliverable> {
      title: { required: true },
      fileNames: { required: true, min: 1, accessor: (deliverable) => deliverable.evidence.fileNames },
      invoiceAdvisorName: { required: true, accessor: (deliverable) => deliverable.invoice.advisor.firstName }
    }
*/

import FormTypes from "../types/forms";
import { isValidName } from "../utils/name";

// NOTE - this plugin is built on top of a mixin because we need reactive data

export default {
  data() {
    return {
      showFormValidationErrors: false
    }
  },
  mounted() {
    window.EventBus.on('FormValidator-validated', () => { (this as any).showFormValidationErrors = true; });
    window.EventBus.on('FormValidator-clear', () => {  (this as any).showFormValidationErrors = false; });
  },
  methods: {
    hideValidationErrors() {
      window.EventBus.emit('FormValidator-clear');
    },
  
    isValidForm(formData: any, validations: FormTypes.Validators): boolean {
      window.EventBus.emit('FormValidator-validated');
      const fields = Object.keys(validations)
      for (const key of fields) {
        const validation = validations[key];
        if (!(key in formData)) {
          // Don't bother validating if the form doesn't actually contain the validation. This is possible for partial updates where we aren't sending the full form in.
          continue;
        }
        let fieldValue = formData[key];
        if (validation.accessor) {
          fieldValue = validation.accessor(formData);
        }
  
        if (validation.validateList) {
          for (let v of fieldValue) {
            if (this._errors(key, v, validation).length) { return false; };
          }
        } else {
          if (this._errors(key, fieldValue, validation).length) { return false; };
        }
      }
  
      return true;
    },
  
    isValidField(val: any, validation: FormTypes.Validator): boolean {
      return this._errors('', val, validation).length == 0;
    },
  
    fieldErrors(key: string, val: any, validation: FormTypes.Validator): Array<string> {
      if (! (this as any).showFormValidationErrors) { return []; }
      return this._errors(key, val, validation);
    },
  
    valPresent(val: any, nonZero?: boolean): boolean {
      if (typeof val == "number") {
        if (val == undefined || val == null || isNaN(parseInt(val.toString()))) { return false; }
        if (nonZero && val == 0) {
          return false;
        }
      } else {
        if (val === undefined || val === null || val === '') { return false; }
      }
  
      return true;
    },
  
    valUniqueWithin(val: any, arr: Array<any>): boolean {
      let isUnique = true;
  
      if (typeof val == "string") { val = val.toLowerCase(); }
  
      let count = 0;
      for (let item of arr) {
        if (typeof item == "string") { item = item.toLowerCase(); }
  
        if (item != val) { continue; }
        count++;
        if (count > 1) {
          isUnique = false;
          break;
        }
      }
  
      return isUnique;
    },
  
    valUnique(arr: Array<any>): boolean {
      let counts: any = {};
      let isUnique = true;
  
      for (let item of arr) {
        if (typeof item == "string") { item = item.toLowerCase(); }
        counts[item] == undefined ? counts[item] = 1 : counts[item] += 1;
        if (counts[item] > 1) {
          isUnique = false;
          break;
        }
      }
  
      return isUnique;
    },
  
    valIn(val: any, arr: Array<any>): boolean {
      let satisfiesIn = false;
      for (let item of arr) {
        if (item == val) {
          satisfiesIn = true;
          break;
        }
      }
      return satisfiesIn;
    },
  
    valIsNumber(val: any): boolean {
      return !isNaN(parseFloat(val.toString()));
    },
  
    valIsName(val: any): boolean {
      return isValidName(val)
    },
  
    _errors(key: string, val: any, validation: FormTypes.Validator): Array<string> {
      const label = validation.label ? validation.label : key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/([A-Z])([a-z])/g, ' $1$2').replace(/\ +/g, ' '); // https://stackoverflow.com/questions/4149276/javascript-camelcase-to-regular-form/4149393
      let errors: Array<string> = [];
  
      if (validation.skip === true || (typeof validation.skip === 'function' && validation.skip())) {
        return errors;
      }
  
      if (validation.required === true || (typeof validation.required === 'function' && validation.required())) {
        if (!this.valPresent(val, validation.nonZero)) { errors.push(`${label} is required`); }
      }
  
      if (validation.maxLength) {
        if (val.length > validation.maxLength) {
          if (typeof val == "string") {
            errors.push(`${label} must be no more than ${validation.maxLength} characters`);
          } else {
            errors.push(`There must be no more than ${validation.maxLength} ${label}`);
          }
        }
      }
  
      if (validation.minLength) {
        if (val.length < validation.minLength) {
          if (typeof val == "string") {
            errors.push(`${label} must be at least ${validation.minLength} characters`);
          } else {
            errors.push(`There must be at least ${validation.minLength} ${label}`);
          }
        }
      }
  
      if (validation.length) {
        if (val.length !== validation.length) {
          if (typeof val == "string") {
            errors.push(`${label} must be ${validation.length} characters`);
          } else {
            errors.push(`There must be ${validation.length} ${label}`);
          }
        }
      }
  
      if (validation.min) {
        const display = validation.currency ?  (this as any).$filters.centsToDollars(validation.min) : validation.min;
        if (val < validation.min) { errors.push(`${label} must be at least ${display}`); }
      }
  
      if (validation.max) {
        const display = validation.currency ?  (this as any).$filters.centsToDollars(validation.max) : validation.max;
        if (val > validation.max) { errors.push(`${label} must be no more than ${display}`); }
      }
  
      if (validation.format) {
        if (!validation.format.test(val)) { errors.push(`${label} is invalid or not formatted correctly`); }
      }
  
      if (validation.email) {
        const emailRe = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (!emailRe.test(val)) { errors.push(`${label} must be a valid email`); }
      }
  
      if (validation.number) {
        if (!this.valIsNumber(val)) { errors.push(`${label} must be a valid number`); }
      }
  
      if (validation.name) {
        if (!this.valIsName(val)) { errors.push(`${label} must be a valid name (space, apostrophe, hyphen allowed)`); }
      }
  
      if (validation.equalTo) {
        if (val != validation.equalTo.val) { errors.push(`${label} must be equal to ${validation.equalTo.label}`); }
      }
  
      if (validation.customValidators) {
        for (let customValidator of validation.customValidators) {
          if (!customValidator.test(val)) { errors.push(customValidator.failMsg(val)); }
        }
      }
  
      if (validation.uniqueWithin) {
        if (!!val) {
          if (!this.valUniqueWithin(val, validation.uniqueWithin)) { errors.push(`Each ${label} must be unique`); }
        }
      }
  
      if (validation.notIn) {
        if (!!val) {
          if (typeof val == "string") { val = val.toLowerCase(); }
  
          for (let item of validation.notIn) {
            if (typeof item == "string") { item = item.toLowerCase(); }
  
            if (item == val) {
              errors.push(`${val} already exists`);
              break;
            }
          }
        }
      }
  
      if (validation.in) {
        if (!this.valIn(val, validation.in)) { errors.push(`${label} is not a permitted value`); }
      }
  
      return errors
    }
  }
}